import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useLocationContext } from "../../contexts/locationContext";
import { handleGetPlans } from "../../shared/controllers/generalController";
import { unwrapApiArray } from "../../shared/utils/apiResponse";
import LoadingGrid from "../../utils/loadingGrid";
import { formatItemPrice } from "../../utils/price";

import check from "./assets/check.svg";

interface Heading {
    category: string;
    title?: string;
    excerpt?: string;
}



interface Plan {
    name: string;
    periodicity_type: string;
    benefits: string[];
    [key: string]: unknown;
}

interface PricingProps {
    headings: Heading[];
}

const Pricing: React.FC<PricingProps> = ({ headings }) => {
    const [activePeriod] = useState("Month");
    const { location: storedLocation } = useLocationContext();

    const getPriceLabel = (plan: Plan) =>
        formatItemPrice(plan, storedLocation.countryCode);

    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(true);
    const [pageTitle, setPageTitle] = useState("Our Plans");
    const [pageExcerpt, setPageExcerpt] = useState(
        "The Perfect Plan to Elevate Your Football Experience"
    );

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const data = await handleGetPlans<Plan[]>();
            setPlans(unwrapApiArray<Plan>(data));
        } catch (error) {
            console.error("Error fetching plans:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    useEffect(() => {
        const planHeading = headings.find((heading) => heading.category === "Plan");
        if (planHeading) {
            setPageTitle(planHeading.title || "Our Plans");
            setPageExcerpt(
                planHeading.excerpt ||
                "The Perfect Plan to Elevate Your Football Experience"
            );
        }
    }, [headings]);

    const normalizedCountry = (storedLocation.countryCode || "").toUpperCase();
    const allowedPlanCountries = ["GB", "GBR", "UK"];
    const isAllowedCountry = allowedPlanCountries.includes(normalizedCountry);

    const filteredPlans =
        isAllowedCountry
            ? plans.filter(
                (item) => item.periodicity_type === activePeriod || item.name === "Free"
            )
            : [];

    return (
        <>
            <div
                id="pricing-section"
                className="flex items-center flex-col py-8 md:py-16 mt-8 md:mt-16 w-full px-4 sm:px-6 md:px-14"
            >
                <h3 className="text-center text-xl sm:text-2xl md:text-3xl font-bold font-Manrope">
                    {pageTitle}
                </h3>
                <h2 className="text-center mt-2 md:mt-3 text-base sm:text-lg md:text-xl font-Manrope font-medium text-gray-600">
                    {pageExcerpt}
                </h2>

                <div className="w-full mt-6 md:mt-8">
                    {!isAllowedCountry ? (
                        <div className="flex w-full flex-row items-center justify-center mt-12">
                            <h3 className="text-center font-Manrope text-base sm:text-lg md:text-xl text-[#344054] font-semibold">
                                Membership plans are currently available to players in the United Kingdom. Switch your location to the UK to view and subscribe.
                            </h3>
                        </div>
                    ) : loading ? (
                        <LoadingGrid count={3} />
                    ) : plans.length === 0 ? (
                        <div className="flex w-full flex-row items-center justify-center mt-12">
                            <h3 className="text-center font-Manrope text-base sm:text-lg md:text-xl text-black font-semibold">
                                There are no plans available at this moment
                            </h3>
                        </div>
                    ) : (
                        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                            {filteredPlans.map((item, index) => (
                                <div
                                    key={index}
                                    className="w-full bg-[#F3F0E9] rounded-[20px] p-4 sm:p-6 md:p-8 flex flex-col h-full"
                                >
                                    <p className="text-[#000] font-Manrope font-bold text-base sm:text-lg md:text-xl lg:text-2xl whitespace-nowrap overflow-hidden text-ellipsis">
                                        {item.name} Plan
                                    </p>
                                    <p className="text-[#000] font-Manrope font-normal text-xl sm:text-2xl md:text-3xl lg:text-[48px] flex items-center mt-3 sm:mt-4">
                                        {getPriceLabel(item)}
                                        <span className="font-Manrope text-[#000] font-normal text-sm sm:text-base md:text-lg lg:text-2xl ml-1 sm:ml-2">
                                            /{item.periodicity_type}
                                        </span>
                                    </p>
                                    <div className="py-4 sm:py-6 space-y-2 sm:space-y-3 flex-1">
                                        {item.benefits.map((feature, idx) => (
                                            <span
                                                key={idx}
                                                className="flex flex-row items-center space-x-2 sm:space-x-3"
                                            >
                                                <img
                                                    src={check}
                                                    className="w-5 sm:w-6 md:w-auto"
                                                    alt="Checkmark"
                                                />
                                                <p className="text-[#000] font-Manrope font-normal text-xs sm:text-sm md:text-base">
                                                    {feature}
                                                </p>
                                            </span>
                                        ))}
                                    </div>
                                    <Link to="/sign-up" className="mt-auto">
                                        <button className="py-3 sm:py-4 px-4 sm:px-6 bg-[#317333] shadow rounded-[6px] text-center shadow-[#1018280A] text-white font-Manrope text-sm sm:text-base md:text-lg font-semibold w-full">
                                            Subscribe
                                        </button>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Pricing;
